import type { ViewKpiAppearance, ViewUiKpiPivotColumn } from '@salescore/core'
import { Array, pipe } from 'effect'

import {
  CELL_BODY_PADDING_LEFT,
  HEADER_CELL_HORIZONTAL_PADDING,
  HEADER_ICON_GRID_GAP,
  MINIMUM_WIDTH,
  ROW_HEIGHT,
  SHEET_HEADER_CELL_HEIGHT,
} from '../rsheet/components/body/const'

const ANTD_DEFAULT_FONT_SIZE = 14
const DATA_ICON_WIDTH = ANTD_DEFAULT_FONT_SIZE
const ANTD_SPACE_GAP = 8
// 2行折り返しでフォントサイズが大きいままになるより、早めにcompactへ切り替える方が見栄えが良いため余裕を持たせる
const COMPACT_TRIGGER_BUFFER = 8
const HEADER_BIG_ICON_BASE_HEIGHT = 62
const HEADER_TITLE_ROW_HEIGHT = 14

// canvas.measureText を使わず文字コードで半角/全角を判別して概算する
const HALF_WIDTH_CHAR_WIDTH = 8
const FULL_WIDTH_CHAR_WIDTH = 14

function estimateTextWidth(text: string): number {
  return pipe(
    Array.fromIterable(text),
    Array.reduce(
      0,
      (width, char) => width + ((char.codePointAt(0) ?? 0) <= 0x7f ? HALF_WIDTH_CHAR_WIDTH : FULL_WIDTH_CHAR_WIDTH),
    ),
  )
}

export type HeaderCompactLevel = 'normal' | 'compact' | 'hidden'

interface IconColumn {
  readonly?: boolean
  isFiltered?: boolean
  sortState?: string
  isHighlighted?: boolean
}

function countIcons(column: IconColumn): number {
  return [
    column.readonly === true,
    column.isFiltered === true,
    column.sortState === 'asc' || column.sortState === 'desc',
    column.isHighlighted === true,
  ].filter(Boolean).length
}

export const getHeaderCompactLevel = (columnWidth: number, title: string, column: IconColumn): HeaderCompactLevel => {
  if (columnWidth <= MINIMUM_WIDTH) return 'hidden'
  const iconCount = countIcons(column)
  const iconColumns = Math.min(Math.ceil(iconCount / 2), 2)
  const iconGridWidth =
    iconColumns > 0 ? ANTD_DEFAULT_FONT_SIZE * iconColumns + HEADER_ICON_GRID_GAP * (iconColumns - 1) : 0
  // Space の子要素: [ColumnIcon, テキスト, HeaderIcons(任意)]
  // gap はアイテム間に入る: ColumnIcon-テキスト間 + テキスト-HeaderIcons間(アイコンがある場合)
  const spaceGapCount = iconCount > 0 ? 2 : 1
  const availableWidth =
    columnWidth -
    DATA_ICON_WIDTH -
    ANTD_SPACE_GAP * spaceGapCount -
    iconGridWidth -
    CELL_BODY_PADDING_LEFT -
    HEADER_CELL_HORIZONTAL_PADDING * 2 // padding は左右両側にかかる
  const estimatedTextWidth = estimateTextWidth(title)
  if (estimatedTextWidth + COMPACT_TRIGGER_BUFFER > availableWidth) return 'compact'
  return 'normal'
}

export interface KpiPivotConfig {
  rows: ViewUiKpiPivotColumn[]
  columns: ViewUiKpiPivotColumn[]
}
export type RSheetContext = SheetTableContext | KpiTableContext | KpiPivotTableContext

export interface SheetTableContext {
  type: 'sheet'
  fixedLeftColumnIndex: number

  isGoalView: boolean
}

export interface KpiTableContext {
  type: 'kpi'
  fixedLeftColumnIndex: number

  pivot: KpiPivotConfig

  kpi: ViewKpiAppearance
}

export interface KpiPivotTableContext {
  type: 'kpiPivot'
  fixedLeftColumnIndex: number

  pivot: KpiPivotConfig

  kpis: ViewKpiAppearance[]
  headerWithBigIcon: boolean
  headerTitleRowNum: number
}

export const buildHeaderCellHeight = (context: RSheetContext): number => {
  if (context.type === 'kpiPivot' && context.headerWithBigIcon) {
    return HEADER_BIG_ICON_BASE_HEIGHT + (context.headerTitleRowNum - 1) * HEADER_TITLE_ROW_HEIGHT
  }
  return context.type === 'sheet' ? SHEET_HEADER_CELL_HEIGHT : ROW_HEIGHT
}

export const isKpiOrKpiPivotContext = (context: RSheetContext): context is KpiTableContext | KpiPivotTableContext =>
  context.type === 'kpi' || context.type === 'kpiPivot'
